import React from 'react';
import PropTypes from 'prop-types';
import { defineMessages, intlShape } from 'react-intl';
import get from 'lodash/get';
import gql from 'graphql-tag';

import MediaCard from '@redbubble/design-system/react/MediaCard';
import * as constants from '@redbubble/design-system/react/constants';

import Favorite from '../Favorite';
import ProductPrice from '../ProductPrice';

import { AnalyticsStorage } from '../../lib/gtm/utils';
import { AnalyticsLocalStorageKeys } from '../../lib/gtm/constants';

const messages = defineMessages({
  artistAttribution: { defaultMessage: 'by {artistName}' }
});

const PREVIEW_TYPE_ID = 'product_close';

const InventoryItemCard = ({
  item,
  toggleFavorite,
  favorited,
  showFavorite,
  isLoggedIn,
  openSignUpModal,
  intl,
  urlRef,
  transparent,
  showStrikethroughPricing,
  index,
  analyticsComponentID
}) => {
  const { description, previewSet, work, productPageUrl } = item;

  const previews = get(previewSet, 'previews', []);
  const primaryPreview = previews.find(
    ({ previewTypeId }) => previewTypeId === PREVIEW_TYPE_ID
  );
  const imageSrc = get(primaryPreview, 'url');

  const href = `${productPageUrl}${urlRef ? `?ref=${urlRef}` : ''}`;

  const handleNonMemberFavorite = () => {
    openSignUpModal(imageSrc);
  };

  const handleFavoriteClick = isLoggedIn
    ? toggleFavorite
    : handleNonMemberFavorite;

  const cardTitle = `${work.title} ${description}`;
  const artistAttribution = intl.formatMessage(messages.artistAttribution, {
    artistName: item.work.artistShopDisplayName
  });

  return (
    <MediaCard
      title={cardTitle}
      caption={artistAttribution}
      element="a"
      href={href}
      imageRatio={constants.RATIO_SQUARE}
      imageCover
      imageSrc={imageSrc}
      transparent={transparent}
      primaryActionSlot={
        showFavorite ? (
          <Favorite favorited={favorited} onClick={handleFavoriteClick} />
        ) : null
      }
      onClick={() => {
        AnalyticsStorage.setValue(
          AnalyticsLocalStorageKeys.RefListID,
          analyticsComponentID || 'unknown_carousel'
        );
        AnalyticsStorage.setValue(
          AnalyticsLocalStorageKeys.RefListIndex,
          String(index)
        );
      }}
    >
      <ProductPrice
        inventoryItem={item}
        showStrikethroughPricing={showStrikethroughPricing}
      />
    </MediaCard>
  );
};

InventoryItemCard.displayName = 'InventoryItemCard';

InventoryItemCard.fragment = gql`
  fragment InventoryItemCard on ihs_InventoryItemsItem {
    productTypeId
    blankItemId
    workId
    description
    previewSet {
      previews {
        previewTypeId
        url
      }
    }
    price {
      amount
      currency
      discount {
        amount
        percent
      }
    }
    volumeDiscount {
      thresholds {
        quantity
        percentOff
      }
    }
    productPageUrl
    work(locale: $locale) {
      artistName
      artistShopDisplayName
      artistId
      title
    }
  }
`;

InventoryItemCard.propTypes = {
  item: PropTypes.shape({
    description: PropTypes.string.isRequired,
    previewSet: PropTypes.shape({
      previews: PropTypes.arrayOf(
        PropTypes.shape({
          previewTypeId: PropTypes.string.isRequired,
          url: PropTypes.string.isRequired
        })
      )
    }).isRequired,
    work: PropTypes.shape({
      title: PropTypes.string.isRequired,
      artistName: PropTypes.string.isRequired
    }).isRequired,
    price: PropTypes.shape({
      amount: PropTypes.number.isRequired,
      currency: PropTypes.string.isRequired
    }).isRequired,
    volumeDiscount: PropTypes.shape({
      thresholds: PropTypes.arrayOf(
        PropTypes.shape({
          quantity: PropTypes.number.isRequired,
          percentOff: PropTypes.string.isRequired
        })
      )
    }),
    productPageUrl: PropTypes.string.isRequired
  }).isRequired,
  toggleFavorite: PropTypes.func,
  favorited: PropTypes.bool,
  showFavorite: PropTypes.bool.isRequired,
  isLoggedIn: PropTypes.bool,
  openSignUpModal: PropTypes.func,
  intl: intlShape.isRequired,
  urlRef: PropTypes.string,
  transparent: PropTypes.bool,
  index: PropTypes.number,
  analyticsComponentID: PropTypes.string
};

InventoryItemCard.defaultProps = {
  toggleFavorite: () => {},
  favorited: false,
  isLoggedIn: false,
  openSignUpModal: () => {},
  urlRef: 'explore',
  transparent: false
};

export default InventoryItemCard;
